'use client';

import { isPlainObject } from 'es-toolkit/predicate';
import type { ReactNode } from 'react';
import { lazy, Suspense, useMemo } from 'react';
import type { Components } from 'react-markdown';
import { StreamdownProvider } from '@/components/markdown/StreamdownProvider';
import { UserPreview } from '@/components/shared/previews/UserPreview';
import {
  EXTENDED_TWEET_ID_PATTERN,
  INLINE_MENTION_CAPTURE_PATTERN,
  RICH_CONTENT_PATTERN,
  USERNAME_PATTERN,
} from '@/lib/utils/regex';

interface MessageParserProperties {
  content: string;
  onSuggestionSelect?: ((text: string) => void) | undefined;
}

type MarkdownNodeLike =
  | {
      properties?: Record<string, unknown>;
    }
  | null
  | undefined;

interface CustomRendererProperties {
  children?: ReactNode;
  node?: MarkdownNodeLike;
}

const transformMentions = (input: string): string =>
  input.replaceAll(
    INLINE_MENTION_CAPTURE_PATTERN,
    (_: string, username: string) => `<chat-user-mention data-username="${username}">@${username}</chat-user-mention>`
  );

const transformRichContentPlaceholders = (input: string): string =>
  input.replaceAll(RICH_CONTENT_PATTERN, (match: string, type: string, rawId: string) => {
    const trimmed = typeof rawId === 'string' ? rawId.trim() : '';
    if (trimmed.length === 0) {
      return match;
    }

    if (type.toLowerCase() === 'tweet') {
      if (!EXTENDED_TWEET_ID_PATTERN.test(trimmed)) {
        return match;
      }
      return `<chat-tweet data-tweet-id="${trimmed}"></chat-tweet>`;
    }

    if (!USERNAME_PATTERN.test(trimmed)) {
      return match;
    }
    return `<chat-user data-username="${trimmed}"></chat-user>`;
  });

const extractAttribute = (node: MarkdownNodeLike, key: string): string | null => {
  const propertiesCandidate = isPlainObject(node) ? (node as { properties?: unknown }).properties : undefined;
  const properties = isPlainObject(propertiesCandidate) ? propertiesCandidate : undefined;
  const descriptor = properties ? Object.getOwnPropertyDescriptor(properties, key) : undefined;
  const value: unknown = descriptor?.value;
  if (typeof value === 'string') {
    return value;
  }
  if (Array.isArray(value)) {
    const first = value.find((entry) => typeof entry === 'string');
    return typeof first === 'string' ? first : null;
  }
  return null;
};

export function MessageParser({ content, onSuggestionSelect }: MessageParserProperties) {
  const transformedContent = useMemo(() => transformRichContentPlaceholders(transformMentions(content)), [content]);

  type ExtendedComponents = Components & {
    'chat-tweet'?: (properties: CustomRendererProperties) => ReactNode;
    'chat-user'?: (properties: CustomRendererProperties) => ReactNode;
    'chat-user-mention'?: (properties: CustomRendererProperties) => ReactNode;
  };

  const markdownComponents = useMemo<ExtendedComponents>(
    () => ({
      'chat-tweet': ({ node }: CustomRendererProperties) => {
        const tweetId = extractAttribute(node ?? null, 'data-tweet-id');
        if (!tweetId) {
          return null;
        }
        return (
          <Suspense fallback={null}>
            <LazyRichContent type="tweet" id={tweetId} />
          </Suspense>
        );
      },
      'chat-user': ({ node }: CustomRendererProperties) => {
        const username = extractAttribute(node ?? null, 'data-username');
        if (!username) {
          return null;
        }
        return (
          <Suspense fallback={null}>
            <LazyRichContent type="user" id={username} />
          </Suspense>
        );
      },
      'chat-user-mention': ({ node, children }: CustomRendererProperties) => {
        const username = extractAttribute(node ?? null, 'data-username');
        if (!username) {
          return <>{children}</>;
        }

        return (
          <UserPreview username={username} allowNestedHover>
            <a href={`/@${username}`} className="inline-flex items-center gap-1 text-primary hover:underline">
              {children ?? `@${username}`}
            </a>
          </UserPreview>
        );
      },
    }),
    []
  );

  const transformAnchor = useMemo(() => {
    if (!onSuggestionSelect) {
      return;
    }
    return ({ url, anchorChildren }: { url: string; anchorChildren: ReactNode }) => {
      if (!url.startsWith('suggest://')) {
        return;
      }
      const decoded = decodeURIComponent(url.slice('suggest://'.length));
      return (
        <button
          type="button"
          onClick={() => {
            onSuggestionSelect(decoded);
          }}
          className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary transition hover:bg-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        >
          {anchorChildren}
        </button>
      );
    };
  }, [onSuggestionSelect]);

  return (
    <StreamdownProvider components={markdownComponents} enablePreviews transformAnchor={transformAnchor}>
      {transformedContent}
    </StreamdownProvider>
  );
}
const LazyRichContent = lazy(async () => {
  const richContentModule = await import('@/components/chat/RichContent');
  return { default: richContentModule.RichContent };
});
